import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { PRODUCT, PRODUCT_KEY } from '@/lib/productIdentity'
import type { Role } from '@/types'

const CURRENT_TOS_VERSION = '1.0'

// ACCESS-TOS-1: the professional-license attestation is a statement about the
// signer's own credentials — only roles that actually hold the license may be
// asked to make it. `tester` is absent because auth normalizes tester → cpa
// before the claim ever reaches the client (see apps/api/auth.py), so a tester
// arrives here already carrying role='cpa'. analyst / firm / super_admin accept
// the general Terms without attesting to a license they may not hold.
const ATTESTATION_ROLES: readonly Role[] = ['cpa']

export function TosGate() {
  const { user, refreshUser, logout } = useAuth()
  const navigate = useNavigate()
  const [tosChecked, setTosChecked] = useState(false)
  const [profChecked, setProfChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const firstCheckboxRef = useRef<HTMLInputElement>(null)

  const needsAttestation = !!user && ATTESTATION_ROLES.includes(user.role)
  const canContinue = tosChecked && (!needsAttestation || profChecked) && !submitting

  // PRODUCT-IDENTITY-1 (I3): this copy predates productIdentity.ts and reads
  // "the QoE Platform", not the registered headline ("Quality of Earnings
  // Platform") used elsewhere — preserved verbatim for QoE so a QoE user's
  // screen does not change at all. Valuation/Attest use the registered name.
  const tosProductName = PRODUCT_KEY === 'qoe' ? 'the QoE Platform' : PRODUCT.headline

  useEffect(() => {
    firstCheckboxRef.current?.focus()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // ACCESS-TOS-1: declining must actually leave. The old Exit navigated to '/',
  // which re-entered ProtectedRoute with tos_accepted still false and re-mounted
  // this same modal — a loop with no way out. Signing out clears the session, so
  // the redirect to /login sticks.
  const handleDecline = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      await logout()
    } finally {
      setSubmitting(false)
      navigate('/login', { replace: true })
    }
  }

  const handleContinue = async () => {
    if (!canContinue) return
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.post('/v1/auth/accept-tos', { version: CURRENT_TOS_VERSION })
      await refreshUser()
    } catch {
      setError('Something went wrong recording your acceptance. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-pal-overlay/80 p-4" role="presentation">
      <Card
        ref={modalRef}
        className="w-full max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tos-title"
        aria-describedby="tos-description"
      >
        <CardHeader className="space-y-3 border-b">
          <CardTitle id="tos-title" className="text-2xl">
            {needsAttestation
              ? 'Terms of Service & Professional Acknowledgment'
              : 'Terms of Service'}
          </CardTitle>
          <CardDescription id="tos-description" className="text-base">
            Please review the following before continuing to {tosProductName}.
          </CardDescription>
        </CardHeader>
        <CardContent className="max-h-96 overflow-y-auto space-y-6 py-6">
          <div className="space-y-3 text-sm leading-relaxed">
            <p>
              By using {tosProductName}, you acknowledge that you have reviewed and agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                Privacy Policy
              </a>.
            </p>
          </div>
          <div className="space-y-4">
            <Label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-accent transition-colors">
              <input
                ref={firstCheckboxRef}
                type="checkbox"
                className="mt-1 size-4 shrink-0 rounded border-input cursor-pointer"
                checked={tosChecked}
                onChange={e => setTosChecked(e.target.checked)}
                aria-label="I have read and agree to the Terms of Service and Privacy Policy"
              />
              <span className="font-normal text-sm">
                I have read and agree to the Terms of Service and Privacy Policy.
              </span>
            </Label>
            {needsAttestation && (
              <Label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-accent transition-colors">
                <input
                  type="checkbox"
                  className="mt-1 size-4 shrink-0 rounded border-input cursor-pointer"
                  checked={profChecked}
                  onChange={e => setProfChecked(e.target.checked)}
                  aria-label="Professional attestation"
                />
                <span className="font-normal text-sm">
                  I confirm I am a licensed CPA, attorney, or financial advisor and will apply
                  my independent professional judgment to all platform outputs. All professional
                  judgments and client communications remain my sole responsibility.
                </span>
              </Label>
            )}
          </div>
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 p-3 rounded">
              {error}
            </p>
          )}
        </CardContent>
        <CardFooter className="border-t justify-end gap-3 pt-6">
          {/* ACCESS-TOS-1: a disabled button with no explanation reads as a broken
              page. Say what is outstanding — and which box, when a CPA has two. */}
          {!canContinue && !submitting && (
            <p className="mr-auto text-sm text-muted-foreground" role="status">
              {needsAttestation && tosChecked && !profChecked
                ? 'Confirm the professional acknowledgment to continue.'
                : 'Accept the Terms to continue.'}
            </p>
          )}
          <Button
            variant="outline"
            disabled={submitting}
            onClick={handleDecline}
          >
            Decline & Sign Out
          </Button>
          <Button
            disabled={!canContinue}
            onClick={handleContinue}
            className="min-w-32"
          >
            {submitting ? 'Saving…' : 'Agree & Continue'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
